import type { SceneObject } from "../SceneHost";
import { objectBounds, type Bounds } from "../selectionBounds";

/**
 * Отсечение объектов за пределами экрана.
 *
 * На загруженной доске рисовались все объекты сразу, включая те, что далеко
 * за краем экрана. Каждое изменение масштаба заставляло браузер
 * перерастеризовать весь слой целиком — отсюда ступенчатый зум.
 *
 * Правила намеренно осторожные: на небольших досках поведение не меняется
 * вообще, а выделенное и живые виджеты не отсекаются никогда — иначе
 * порвались бы перетаскивание, выделение и попадание курсором,
 * которые ищут объекты по узлам DOM.
 */

/**
 * Ниже этого числа объектов отсечение не окупается.
 * Замер на реальной доске: 217 объектов и 31 foreignObject уже заметно
 * тормозят масштабирование, так что прежний порог 300 просто не срабатывал.
 */
export const CULL_MIN_OBJECTS = 80;

/** Запас вокруг экрана в долях его размера: пан на экран не оголяет сцену. */
export const CULL_MARGIN_RATIO = 0.75;

/**
 * Типы с живым состоянием, которое не восстановить из данных: звук
 * оборвётся, отсчёт сорвётся, набранное в поле пропадёт.
 *
 * PDF здесь сознательно НЕТ: его состояние — номер страницы — лежит в
 * объекте, а рисуется он двумя foreignObject с растровым превью и полосой
 * слайдов. На доске с презентациями это самая дорогая часть кадра, и
 * отсекать её за экраном не только можно, но и нужно.
 */
const ALWAYS_RENDER_TYPES = new Set(["timer", "audio", "code", "sql", "calculator"]);

export type CullContext = {
  /** Видимая область в координатах сцены. Без неё отсечение отключается. */
  view?: Bounds | null;
  selectedIds?: Set<string> | null;
  /** Объект в режиме редактирования текста. */
  editingId?: string | null;
};

function intersects(a: Bounds, b: Bounds): boolean {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

/** Расширяет видимую область на запас, чтобы объекты не появлялись рывком у края. */
export function expandView(view: Bounds, ratio = CULL_MARGIN_RATIO): Bounds {
  const mx = view.w * ratio;
  const my = view.h * ratio;
  return { x: view.x - mx, y: view.y - my, w: view.w + mx * 2, h: view.h + my * 2 };
}

/** Нужно ли рисовать объект при текущей камере. */
export function shouldRenderForView(
  obj: SceneObject,
  expanded: Bounds,
  ctx: CullContext
): boolean {
  if (ctx.selectedIds?.has(obj.id)) return true;
  if (ctx.editingId && ctx.editingId === obj.id) return true;
  if (ALWAYS_RENDER_TYPES.has(obj.type)) return true;
  // Рамки задают контекст вложенности и обычно крупнее экрана.
  if (obj.type === "frame") return true;

  const b = objectBounds(obj);
  if (!Number.isFinite(b.x) || !Number.isFinite(b.y)) return true;
  return intersects(b, expanded);
}

/**
 * Отбирает объекты для отрисовки. Возвращает исходный массив без изменений,
 * когда отсекать нечего или незачем — вызывающий код может сравнить по ссылке.
 */
export function cullSceneObjects(objects: SceneObject[], ctx: CullContext): SceneObject[] {
  if (!ctx.view) return objects;
  if (objects.length < CULL_MIN_OBJECTS) return objects;

  const expanded = expandView(ctx.view);
  const out: SceneObject[] = [];
  for (const obj of objects) {
    if (shouldRenderForView(obj, expanded, ctx)) out.push(obj);
  }
  return out.length === objects.length ? objects : out;
}
